import { useCallback } from "react";
import jsPDF from "jspdf";
import { useIntl } from "umi";
import html2canvas from 'html2canvas';

interface IPdfOptions {
  filename?: string;
  watermark?: string | string[];       // support multiple lines
  orientation?: "p" | "l";             // portrait / landscape
  unit?: "pt" | "mm" | "cm" | "in";
  format?: "a3" | "a4" | "letter" | [number, number];
  opacity?: number;                     // 0 to 1
  fontSize?: number;                    // watermark font size
  color?: [number, number, number];     // RGB
}

export interface IPngOptions {
  /** 文件名，默认 export.png；不带 .png 后缀会自动补 */
  filename?: string;
  /** html2canvas 的 scale，默认 2 */
  scale?: number;
  /** 画布背景色，默认 #ffffff */
  backgroundColor?: string;
  /** 截图前需要"撑开"为完整高度的滚动容器选择器（默认空数组，由调用方按业务传入） */
  expandSelectors?: string[];
  /** 截图时需要临时隐藏的元素选择器（默认 ['[data-export-hidden]']，通用开关） */
  hideSelectors?: string[];
  /** 截图四周追加的留白（CSS 像素），数字代表四边相同；对象可分别指定。默认 { bottom: 40 } */
  padding?: number | { top?: number; right?: number; bottom?: number; left?: number; };
  /**
   * 截图前的自定义钩子（在 expand/hide 之后、html2canvas 之前调用）。
   * 可用于添加业务专属的临时样式（例如给表格临时加底边线）。
   * 返回的清理函数会在截图完成后（finally 阶段）执行。
   */
  onBeforeCapture?: (element: HTMLElement) => void | (() => void) | Promise<void | (() => void)>;
}

// 需要避免分页割裂的元素信息
interface AvoidBreakElement {
  top: number;
  bottom: number;
  height: number;
  type: string;
}

// 创建导出蒙层 - 修改函数名使其更通用
const createExportOverlay = (isEn?: boolean, exportType: 'PDF' | 'Word' | 'PNG' = 'PDF'): HTMLElement => {
  const overlay = document.createElement('div');
  overlay.id = `${exportType.toLowerCase()}-export-overlay`;
  overlay.style.cssText = `
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 9999;
  box-sizing: border-box;
  background: #ffffffb3;
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  `;

  const loadingText = document.createElement('div');
  loadingText.style.cssText = `
  background-color: rgba(255, 255, 255, 0.9);
  padding: 20px 40px;
  border-radius: 8px;
  font-size: 16px;
  color: #333;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;

  // 根据导出类型显示不同的文本
  let loadingTextContent: string;
  if (exportType === 'PDF') {
    loadingTextContent = isEn ? 'Exporting PDF...' : '正在导出PDF...';
  } else if (exportType === 'PNG') {
    loadingTextContent = isEn ? 'Exporting Image...' : '正在导出图片...';
  } else {
    loadingTextContent = isEn ? 'Exporting Word...' : '正在导出Word...';
  }

  loadingText.textContent = loadingTextContent;
  overlay.appendChild(loadingText);
  document.body.appendChild(overlay);
  return overlay;
};

// 移除导出蒙层 - 修改函数使其更通用
const removeExportOverlay = (exportType: 'PDF' | 'Word' | 'PNG' = 'PDF'): void => {
  const overlay = document.getElementById(`${exportType.toLowerCase()}-export-overlay`);
  overlay?.parentNode?.removeChild(overlay);
};

// PNG 导出用的 attribute 名称。
// 用属性切换 + class-style override 替代逐个 inline style 写入：
//  - 代码更短、清理更简单（只需 removeAttribute）
//  - 不污染元素既有内联样式
// 注意：CSS 规则**仅在导出期间临时注入**到 head，结束立刻移除。
//      不能永久注入 —— ImageDownloadButton 自身就挂着 data-export-hidden 作为"截图时该隐藏"
//      的标记，如果 CSS 规则常驻，按钮平时也会被永久隐藏。
const PNG_EXPORT_STYLE_ID = '__db_png_export_style__';
const PNG_EXPORT_EXPANDING_ATTR = 'data-export-expanding';
const PNG_EXPORT_HIDDEN_ATTR = 'data-export-hidden';

const injectPngExportStyle = (): HTMLStyleElement => {
  const existed = document.getElementById(PNG_EXPORT_STYLE_ID) as HTMLStyleElement | null;
  if (existed) return existed;
  const style = document.createElement('style');
  style.id = PNG_EXPORT_STYLE_ID;
  style.textContent = `
    [${PNG_EXPORT_EXPANDING_ATTR}] {
      max-height: none !important;
      height: auto !important;
      overflow: visible !important;
      overflow-x: visible !important;
      overflow-y: visible !important;
    }
    [${PNG_EXPORT_HIDDEN_ATTR}] {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
  return style;
};

const removePngExportStyle = (): void => {
  const style = document.getElementById(PNG_EXPORT_STYLE_ID);
  style?.parentNode?.removeChild(style);
};

// 将 dba-agent-bid 元素转换为图片的函数
const convertBidElementsToImages = async (element: HTMLElement): Promise<string[]> => {
  // 先在原始DOM中找到所有 dba-agent-bid 元素
  const originalBidElements = element.querySelectorAll('.dba-agent-bid');

  // 存储转换后的图片数据
  const imageDataUrls: string[] = [];

  // 将每个原始 dba-agent-bid 元素转换为图片（不修改原始元素）
  const conversionPromises = Array.from(originalBidElements).map(async (bidElement, index) => {
    try {
      // 确保元素在DOM中且可见
      const elementRect = (bidElement as HTMLElement).getBoundingClientRect();
      if (elementRect.width === 0 || elementRect.height === 0) {
        console.warn(`第 ${index + 1} 个图表元素尺寸为0，跳过转换`);
        imageDataUrls[index] = '';
        return;
      }

      // 使用 html2canvas 将原始元素转换为图片（只读取，不修改）
      const canvas = await html2canvas(bidElement as HTMLElement, {
        useCORS: true,
        allowTaint: false,
        scale: 2, // 提高图片质量
        backgroundColor: '#ffffff',
        logging: false,
        foreignObjectRendering: false,
        removeContainer: false,
        width: elementRect.width,
        height: elementRect.height,
        // 添加这些选项来确保正确渲染
        ignoreElements: (element) => 
          // 忽略一些可能影响渲染的元素
          element.classList?.contains('dba-bid-ops') || false
        ,
        onclone: (clonedDoc, clonedElement) => {
          // 在克隆的文档中确保样式正确
          const clonedBidElements = clonedElement.querySelectorAll('.dba-agent-bid');
          clonedBidElements.forEach((el) => {
            const htmlEl = el as HTMLElement;
            htmlEl.style.position = 'static';
            htmlEl.style.transform = 'none';
            htmlEl.style.zIndex = 'auto';
          });
        }
      });

      // 将 canvas 转换为 base64 图片
      const imageDataUrl = canvas.toDataURL('image/png', 1.0);
      imageDataUrls[index] = imageDataUrl;
    } catch (error) {
      console.log(`转换第 ${index + 1} 个图表元素失败:`, error);
      imageDataUrls[index] = '';
    }
  });

  // 等待所有转换完成
  await Promise.all(conversionPromises);

  return imageDataUrls;
};

// 替换克隆元素中的图表为图片的函数
const replaceBidElementsWithImages = (clonedElement: HTMLElement, imageDataUrls: string[]): void => {
  const clonedBidElements = clonedElement.querySelectorAll('.dba-agent-bid');

  // 用转换后的图片替换克隆元素中的图表（原始元素保持不变）
  clonedBidElements.forEach((bidElement, index) => {
    const imageDataUrl = imageDataUrls[index];
    if (imageDataUrl) {
      // 创建新的 img 元素替换原来的 dba-agent-bid 元素（只在克隆的元素中）
      const imgElement = document.createElement('img');
      imgElement.src = imageDataUrl;
      // imgElement.alt = `图表 ${index + 1}`;
      imgElement.style.width = '100%';
      imgElement.style.height = 'auto';
      imgElement.style.display = 'block';
      // imgElement.style.margin = '10px 0';
      // imgElement.style.border = 'none';
      // imgElement.style.borderRadius = '8px';

      // 替换原始元素（只在克隆的元素中替换）
      bidElement.parentNode?.replaceChild(imgElement, bidElement);
    } else {
      // 如果转换失败，隐藏该元素（只在克隆的元素中隐藏）
      (bidElement as HTMLElement).style.display = 'none';
    }
  });
};

/**
 * 通过 fetch 将图片转换为 base64 dataURL
 */
const fetchImageAsDataURL = async (url: string): Promise<string | null> => {
  try {
    // dataURL 直接返回
    if (url.startsWith('data:')) return url;
    const res = await fetch(url, { mode: 'cors' as RequestMode });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null as any);
      reader.onerror = () => reject(new Error('reader error'));
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    // CORS 或网络失败
    return null;
  }
};

/**
 * 预加载并尽可能将图片转换为 base64，避免 Word 无法外链渲染
 */
const preloadImages = async (images: NodeListOf<Element>): Promise<void> => {
  const imageLoadPromises = Array.from(images).map(async (img: Element) => {
    const imgElement = img as HTMLImageElement;

    if (!imgElement.src) return;

    imgElement.removeAttribute('srcset');
    imgElement.removeAttribute('sizes');

    if (imgElement.src.startsWith('data:')) return;

    // 优先通过 fetch 转 dataURL
    const dataUrl = await fetchImageAsDataURL(imgElement.src);
    if (dataUrl) {
      imgElement.src = dataUrl;
      return;
    }

    // 确保图片完全加载后再转换
    await new Promise<void>((resolve) => {
      const newImg = new Image();
      newImg.crossOrigin = 'anonymous';
      
      // 添加重试控制变量
      let retryCount = 0;
      const maxRetries = 3; // 最多重试3次
      const timeout = 5000; // 5秒超时
      const startTime = Date.now();
      
      const convertImage = () => {
        if (newImg.naturalWidth > 0 && newImg.naturalHeight > 0) {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (ctx) {
              canvas.width = newImg.naturalWidth;
              canvas.height = newImg.naturalHeight;
              ctx.drawImage(newImg, 0, 0);
              const base64 = canvas.toDataURL('image/png');
              imgElement.src = base64;
            }
          } catch (error) {
            console.warn('图片转换失败:', error);
          }
          resolve();
        } else {
          // 检查重试条件
          if (retryCount < maxRetries && Date.now() - startTime < timeout) {
            retryCount += 1;
            setTimeout(convertImage, 50);
          } else {
            console.warn(`图片转换超时或重试次数过多: ${imgElement.src}, 重试次数: ${retryCount}`);
            resolve(); // 即使失败也要 resolve
          }
        }
      };
      
      newImg.onload = convertImage;
      newImg.onerror = () => {
        console.warn('图片加载失败:', imgElement.src);
        resolve();
      };
      
      newImg.src = imgElement.src;
    });
  });

  await Promise.all(imageLoadPromises);
};

/**
 * 处理图片尺寸和样式
 */
const processImageElement = (img: HTMLImageElement): HTMLElement => {
  // 检查是否包含 not-reset-css 类，如果包含则不进行样式处理
  if (img.classList.contains('dba-ds-report-top-logo-word-img')) {

    // 添加p标签并设置align为right，使图片右对齐
    const wrapper = document.createElement('p');
    wrapper.setAttribute('align', 'right');
    // 克隆原始图片并添加到包装器中
    const clonedImg = img.cloneNode(true) as HTMLImageElement;
    // 宽度设置auto的话mac上宽高不生效
    clonedImg.setAttribute('width', '133');
    clonedImg.setAttribute('height', '28');
    clonedImg.setAttribute('style', `width: 28px; height: 133px; display: block;`);
    wrapper.appendChild(clonedImg);

    return wrapper;
  }
  // 移除现有的style属性
  img.removeAttribute('style');

  // 安全地获取图片尺寸
  const originalWidth = img.naturalWidth;
  const originalHeight = img.naturalHeight;

  // 如果 naturalWidth 为 0，直接隐藏该图片
  if (originalWidth === 0 || originalHeight === 0) {
    console.warn('图片尺寸为0，隐藏该图片', img.src);
    const hidden = document.createElement('div');
    hidden.style.display = 'none';
    return hidden; // 提前返回隐藏元素，后续不再处理
  }

  const maxWidth = 900;
  let width = originalWidth;
  let height = originalHeight;

  if (originalWidth > maxWidth) {
    width = maxWidth;
    height = (originalHeight * maxWidth) / originalWidth;
  }

  // 创建新的图片元素
  const newImg = document.createElement('img');
  newImg.src = img.src;
  newImg.alt = img.alt || '';
  newImg.setAttribute('width', width.toString());
  newImg.setAttribute('height', Math.round(height).toString());
  newImg.setAttribute('style', `width: ${width}px; height: ${Math.round(height)}px; display: block;`);

  // 使用 div 包装图片
  const wrapper = document.createElement('div');
  wrapper.setAttribute('style', 'text-align: center; margin: 10px 0;');
  wrapper.appendChild(newImg);

  return wrapper;
};

/**
 * 处理所有图片元素
 */
const processAllImages = (clonedElement: HTMLElement): void => {
  const imageElements = clonedElement.querySelectorAll('img');
  imageElements.forEach((img: HTMLImageElement) => {
    const wrapper = processImageElement(img);
    img.parentNode?.replaceChild(wrapper, img);
  });
};

/**
 * 生成 Word 文档的 HTML 模板
 */
const generateWordDocumentTemplate = (content: string): string => {

  // xml配置作用：打印视图：强制 Word 以打印视图打开；缩放 100%：固定显示比例；不优化浏览器：保持 Word 原生格式
  //  @page WordSection1 {
  //   size: 8.5in 11.0in;          /* 信纸尺寸 */
  //   margin: 1.0in 1.0in 1.0in 1.0in;  /* 1英寸边距 */
  //   mso-header-margin: .5in;     /* 页眉边距 */
  //   mso-footer-margin: .5in;     /* 页脚边距 */
  //   mso-paper-source: 0;
  // }
  // div.WordSection1 {
  //   page: WordSection1;          /* 应用页面样式 */
  // }

  const docHeader = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" 
      xmlns:w="urn:schemas-microsoft-com:office:word" 
      xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8" />
        <title>Export</title>
        <style>
          /* 基础表格样式 */
          table { 
            border-collapse: collapse; 
            /* Word 专用样式 */
            mso-table-lspace: 0pt;
            mso-table-rspace: 0pt;
          }
          .data-table {
            width: 100%; 
            border: 1px solid #ccc;
          }
          .image-table {
            width: 100% !important;
            border: none !important;
          }
          .image-table td {
            border: none !important;
            text-align: center;
            padding: 0;
          }
          th, td { 
            padding: 8px; 
            vertical-align: top; 
            border: 1px solid #ccc;
          }
          img {
            page-break-inside: avoid;
          }
          /* Word 专用样式 */
         /* Word 专用样式 */
        @page WordSection1 {
          size: 11.69in 16.53in;  /* A3: 297mm × 420mm */
          margin: 1.0in 1.0in 1.0in 1.0in;
          mso-header-margin: .5in;
          mso-footer-margin: .5in;
          mso-paper-source: 0;
        }
        div.WordSection1 {
          page: WordSection1;
        }
          h1,h2{
            font-size: 20px;
            font-weight: bold;
            color: #262832;
          }
          h3,h4,h5,h6{
            font-size: 16px;
            color: #262832;
          }
        </style>
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
            <w:DoNotOptimizeForBrowser/>
          </w:WordDocument>
        </xml>
        <![endif]-->
      </head>
      <body>
  <div class="WordSection1">`;

  const docFooter = `</div></body></html>`;

  return `${docHeader}${content}${docFooter}`;
};
/**
 * 收集需要避免分页割裂的元素（图片、表格、段落等）
 */
const collectAvoidBreakElements = (
  element: HTMLElement,
  elementRect: DOMRect
): AvoidBreakElement[] => {
  const avoidBreakElements: AvoidBreakElement[] = [];

  // 收集图片
  const imageElements = element.querySelectorAll('img');
  imageElements.forEach((img) => {
    const rect = img.getBoundingClientRect();
    const relativeTop = rect.top - elementRect.top + element.scrollTop;
    const relativeBottom = relativeTop + rect.height;

    avoidBreakElements.push({
      top: relativeTop,
      bottom: relativeBottom,
      height: rect.height,
      type: 'image',
    });
  });

  // 收集表格（整个表格作为一个整体，避免表格被分页割裂）
  const tables = element.querySelectorAll('table');
  tables.forEach((table) => {
    const rect = table.getBoundingClientRect();
    if (rect.height > 0) {
      const relativeTop = rect.top - elementRect.top + element.scrollTop;
      const relativeBottom = relativeTop + rect.height;

      avoidBreakElements.push({
        top: relativeTop - 50,
        bottom: relativeBottom,
        height: rect.height,
        type: 'table',
      });
    }
  });

  // 收集段落和标题
  const textBlocks = element.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, div.dba-agent-bid');
  textBlocks.forEach((block) => {
    const rect = block.getBoundingClientRect();
    // 只处理高度在合理范围内的文本块（避免处理过大的容器）
    if (rect.height > 10 && rect.height < 500) {
      const relativeTop = rect.top - elementRect.top + element.scrollTop;
      const relativeBottom = relativeTop + rect.height;

      avoidBreakElements.push({
        top: relativeTop - 50,
        bottom: relativeBottom,
        height: rect.height,
        type: 'text-block',
      });
    }
  });

  // 按 top 位置排序
  avoidBreakElements.sort((a, b) => a.top - b.top);

  return avoidBreakElements;
};

/**
 * 智能计算分页点，避免割裂任何元素（图片、表格行、段落等）
 */
const calculateSmartPageBreaks = (
  imgHeight: number,
  contentHeight: number,
  scale: number,
  elementHeight: number,
  avoidBreakElements: AvoidBreakElement[]
): number[] => {
  const pageBreaks: number[] = [0]; // 第一页从 0 开始
  let currentY = 0;
  const canvasToElementRatio = imgHeight / elementHeight;
  const pageHeightInElement = contentHeight / scale;

  while (currentY < imgHeight) {
    let nextBreak = currentY + contentHeight / scale;

    // 检查这个分页点是否会割裂任何元素
    const breakPositionInElement = nextBreak / canvasToElementRatio;
    let bestAdjustment: { position: number; distance: number; } | null = null;

    for (const elem of avoidBreakElements) {
      // 如果分页点在元素中间（留出 5px 的容差）
      if (breakPositionInElement > elem.top + 5 && breakPositionInElement < elem.bottom - 5) {
        // 元素高度小于页面高度的 85%，可以调整分页点
        if (elem.height < elementHeight * 0.85) {
          // 计算调整到元素前面的位置
          const adjustedBreakBefore = elem.top * canvasToElementRatio;
          const distanceBefore = breakPositionInElement * canvasToElementRatio - adjustedBreakBefore;

          // 计算调整到元素后面的位置
          const adjustedBreakAfter = elem.bottom * canvasToElementRatio;
          const distanceAfter = adjustedBreakAfter - breakPositionInElement * canvasToElementRatio;

          // 选择距离更近且合理的调整方案
          // 优先选择向前调整（避免页面空白过多），但如果向前调整距离太远（超过页面高度的40%），则选择向后
          if (distanceBefore < pageHeightInElement * 0.4) {
            // 向前调整，且距离当前页起点足够远（至少20%页面高度）
            if (adjustedBreakBefore - currentY > pageHeightInElement * 0.2) {
              if (!bestAdjustment || distanceBefore < bestAdjustment.distance) {
                bestAdjustment = { position: adjustedBreakBefore, distance: distanceBefore };
              }
            } else {
              // 向前调整距离太近，选择向后调整
              if (!bestAdjustment || distanceAfter < bestAdjustment.distance) {
                bestAdjustment = { position: adjustedBreakAfter, distance: distanceAfter };
              }
            }
          } else {
            // 向前调整距离太远，选择向后调整
            if (!bestAdjustment || distanceAfter < bestAdjustment.distance) {
              bestAdjustment = { position: adjustedBreakAfter, distance: distanceAfter };
            }
          }
        }
      }
    }

    // 应用最佳调整方案
    if (bestAdjustment) {
      nextBreak = bestAdjustment.position;
    }

    // 确保不超出总高度
    if (nextBreak >= imgHeight) {
      break;
    }

    currentY = nextBreak;
    pageBreaks.push(currentY);
  }

  return pageBreaks;
};

/**
 * 渲染单个 PDF 页面
 */
const renderPdfPage = (
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  sourceY: number,
  sourceHeight: number,
  imgWidth: number,
  margin: number,
  contentWidth: number,
  scale: number
): void => {
  // 创建临时 canvas 来裁剪图片
  const pageCanvas = document.createElement('canvas');
  pageCanvas.width = imgWidth;
  pageCanvas.height = sourceHeight;
  const pageCtx = pageCanvas.getContext('2d');

  if (pageCtx) {
    pageCtx.drawImage(
      canvas,
      0,
      sourceY,
      imgWidth,
      sourceHeight,
      0,
      0,
      imgWidth,
      sourceHeight
    );

    const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.98);
    pdf.addImage(
      pageImgData,
      'JPEG',
      margin,
      margin,
      contentWidth,
      sourceHeight * scale
    );
  }
};

/**
 * 添加网格状水印到 PDF 页面
 */
const addWatermarkToPage = (
  pdf: jsPDF,
  watermark: string | string[],
  opacity: number,
  fontSize: number | undefined,
  color: [number, number, number]
): void => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // 应用透明度
  const gState = new (pdf as any).GState({ opacity });
  (pdf as any).setGState(gState);

  const watermarkText = Array.isArray(watermark) ? watermark.join(' ') : watermark;
  const [r, g, b] = color;

  const diagonal = Math.sqrt(pageWidth ** 2 + pageHeight ** 2);
  const effectiveFontSize = fontSize || diagonal / 6;

  pdf.setFontSize(effectiveFontSize);
  (pdf as any).setFont('helvetica', 'bold');
  pdf.setTextColor(r, g, b);

  // 5行4列的网格水印
  const maxRows = 5;
  const maxCols = 4;
  const stepY = pageHeight / (maxRows + 1);
  const stepX = pageWidth / (maxCols + 1);

  for (let row = 0;row < maxRows;row++) {
    const y = stepY * (row + 1);
    for (let col = 0;col < maxCols;col++) {
      const x = stepX * (col + 1);
      pdf.text(watermarkText, x, y, {
        angle: 22.5,
        align: 'center',
      });
    }
  }

  // 重置透明度
  (pdf as any).setGState(new (pdf as any).GState({ opacity: 1 }));
};

/**
 * 下载文件
 */
const downloadFile = (content: string, filename: string, mimeType: string): void => {
  const blob = new Blob(["\ufeff", content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * 基于内容像素宽高计算 PDF 尺寸与边距
 * - 保持 A 系列比例（1:√2）
 * - 自动判断横纵向
 * - 固定 marginPt=10，bleed=margin*2.5 + 35*2（与你现逻辑一致）
 */
const calcPdfFormatByContent = (
  contentWidthPx: number
) => {
  const pxToPt = (px: number): number => (px * 72) / 96;

  // 以内容宽度为基准，按 A 系列比例取纸张高宽
  const pageWidthPt = pxToPt(contentWidthPx);
  const pageHeightPt = pageWidthPt * Math.SQRT2;

  // 页面内边距（给 html2pdf 的 margin，pt）
  const marginPt = 10;

  // 安全余量（与你现有注释一致）
  // 应该是乘2，但是避免裁切，所以乘2.5，35是导出内容的padding
  const bleedXPt = pxToPt(marginPt * 2.5 + 35 * 2);
  const bleedYPt = pxToPt(marginPt * 2.5 + 35 * 2);

  const format: [number, number] = [
    Math.ceil(pageWidthPt)  + bleedXPt,
    Math.ceil(pageHeightPt) + bleedYPt,
  ];

  return { format, marginPt };
};

// const getPDfFormat = (element: HTMLElement) => {
//   const width = Number.parseFloat(getComputedStyle(element).getPropertyValue('width'));
//   const height = Number.parseFloat(getComputedStyle(element).getPropertyValue('height'));
//   const pxToPt = (px: number) => (px * 72) / 96;
//   const wPt = pxToPt(width);
//   const hPt = pxToPt(height);
//   return [wPt, hPt] as [number, number];
// };

/**
 * React hook for exporting DOM elements to PDF with oversized grid watermark (cutoff effect)
 */
export const useContentExporter = () => {
  // 中英文
  const { locale } = useIntl();
  const isEn = locale === 'en-US';
  const exportPdf = useCallback(
    async (element: HTMLElement, options?: IPdfOptions): Promise<void> => {
      // 显示蒙层
      createExportOverlay(isEn, 'PDF');

      // 设置为A3纸张宽度 (297mm = 841.89pt ≈ 1122px)
      const exportWidth = Number.parseFloat(getComputedStyle(element).getPropertyValue('width'));
      element.style.width = `${exportWidth}px`;
      const resizeEvent = new Event('resize');
      window.dispatchEvent(resizeEvent);

      // 添加延迟，确保图表完全渲染
      await new Promise((resolve) => setTimeout(resolve, 300));
      const { format: formatValue, marginPt } = calcPdfFormatByContent(exportWidth);

      // 临时隐藏表格操作按钮元素
      const tableActionsElements = element.querySelectorAll('.dba-table-actions');
      const originalDisplayStyles: string[] = [];
      tableActionsElements.forEach((el, index) => {
        const htmlEl = el as HTMLElement;
        originalDisplayStyles[index] = htmlEl.style.display;
        htmlEl.style.display = 'none';
      });

      try {
        const {
          filename = 'export.pdf',
          watermark = 'CONFIDENTIAL',
          orientation = 'p',
          unit = 'pt',
          // format = 'a3',
          opacity = 0.1,
          fontSize = 10,
          color = [150, 150, 150],
        } = options || {};

        // 1. 收集所有需要避免割裂的元素位置信息（图片、表格行、段落等）
        const elementRect = element.getBoundingClientRect();
        const avoidBreakElements = collectAvoidBreakElements(element, elementRect);

        // 2. 使用 html2canvas 将 DOM 转换为 canvas
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          logging: true,
          backgroundColor: '#ffffff',
          foreignObjectRendering: false,
          removeContainer: false,
        });

        // 3. 获取 canvas 尺寸
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        // 4. 创建 PDF 实例
        // eslint-disable-next-line new-cap
        const pdf = new jsPDF({
          unit,
          format: formatValue,
          orientation,
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // 计算内容区域（减去边距）
        const margin = 5;
        const contentWidth = pageWidth - 2 * margin;
        const contentHeight = pageHeight - 2 * margin;

        // 5. 计算缩放比例
        const scale = contentWidth / imgWidth;

        // 6. 智能计算分页点，避免割裂任何元素（图片、表格行、段落等）
        const elementHeight = element.scrollHeight;
        const pageBreaks = calculateSmartPageBreaks(
          imgHeight,
          contentHeight,
          scale,
          elementHeight,
          avoidBreakElements
        );

        // 7. 根据智能分页点添加图片到 PDF
        for (let i = 0;i < pageBreaks.length;i++) {
          if (i > 0) {
            pdf.addPage();
          }

          const sourceY = pageBreaks[i];
          const nextPageY = i < pageBreaks.length - 1 ? pageBreaks[i + 1] : imgHeight;
          const sourceHeight = nextPageY - sourceY;

          // 渲染页面内容
          renderPdfPage(
            pdf,
            canvas,
            sourceY,
            sourceHeight,
            imgWidth,
            marginPt,
            contentWidth,
            scale
          );

          // 添加水印
          addWatermarkToPage(pdf, watermark, opacity, fontSize, color);
        }

        // 8. 保存 PDF
        pdf.save(filename);
      } catch (err) {
        throw err instanceof Error ? err : new Error('Failed to export PDF');
      } finally {
        // 恢复表格操作按钮元素的显示状态
        tableActionsElements.forEach((el, index) => {
          const htmlEl = el as HTMLElement;
          htmlEl.style.display = originalDisplayStyles[index] || '';
        });

        // 还原原始宽度
        element.style.width = `auto`;

        const resizeEvent = new Event('resize');
        window.dispatchEvent(resizeEvent);

        // 移除导出蒙层
        removeExportOverlay('PDF');
      }
    },
    [isEn]
  );

  const exportTxt = useCallback(async (element: HTMLElement, filename = "export.txt"): Promise<void> => {
    const rawText = element?.innerText ?? "";
    // normalize line breaks for Windows Notepad compatibility
    const normalized = rawText.replace(/\r?\n/g, "\r\n");
    // prepend BOM to preserve UTF-8 in Windows
    const blob = new Blob(["\ufeff", normalized], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename.toLowerCase().endsWith(".txt") ? filename : `${filename}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);


  const exportWord = useCallback(async (element: HTMLElement, filename = "export.doc"): Promise<void> => {
    // 临时隐藏表格操作按钮元素
    const tableActionsElements = element.querySelectorAll('.dba-table-actions');
    const originalDisplayStyles: string[] = [];
    tableActionsElements.forEach((el, index) => {
      const htmlEl = el as HTMLElement;
      originalDisplayStyles[index] = htmlEl.style.display;
      htmlEl.style.display = 'none';
    });

    try {
      // 显示导出遮罩
      // createExportOverlay(isEn, 'Word');
      createExportOverlay(isEn, 'Word');
      // 让出渲染机会（两次 rAF 更稳）
      // 留给浏览器渲染一帧后再继续其他操作，避免遮罩有时候没有立即弹出
      // 因为 requestAnimationFrame 的回调是在"下一帧绘制前"执行的。嵌套两次可以确保中间至少发生一次实际的浏览器绘制（paint），从而让前一步对 DOM/样式的变更（比如显示遮罩）先被渲染到屏幕上，再继续后续的重任务/测量。
      // 相比 setTimeout，这种"双 rAF"能更可靠地"等一个实际渲染帧"，适合需要"先让 UI 显示，再干重活"的场景。
      await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())));
      // 1. 转换图表元素为图片
      const imageDataUrls = await convertBidElementsToImages(element);
      // 2. 克隆元素并替换图表
      const clonedElement = element.cloneNode(true) as HTMLElement;
      replaceBidElementsWithImages(clonedElement, imageDataUrls);

      // 3. 移除不需要的元素
      const bidOpsDivs = clonedElement.querySelectorAll('.dba-bid-ops');
      bidOpsDivs.forEach((item: Element) => {
        item?.remove();
      });

      // 4. 预加载并强制将所有图片转为内联 base64，避免 Word 外链占位
      const images = clonedElement.querySelectorAll('img');
      await preloadImages(images);

      // 5. 处理图片样式
      processAllImages(clonedElement);
      // 6. 生成 Word 文档
      const content = clonedElement.innerHTML;
      const html = generateWordDocumentTemplate(content);

      // 7. 下载文件
      const finalFilename = filename.toLowerCase().endsWith(".doc") || filename.toLowerCase().endsWith(".docx") ? filename : `${filename}.doc`;
      downloadFile(html, finalFilename, "application/msword");

    } catch (error) {
      console.error('导出 Word 文档失败:', error);
      throw error;
    } finally {
      // 恢复表格操作按钮元素的显示状态
      tableActionsElements.forEach((el, index) => {
        const htmlEl = el as HTMLElement;
        htmlEl.style.display = originalDisplayStyles[index] || '';
      });

      // 移除导出遮罩
      removeExportOverlay('Word');
    }
  }, [isEn]);

  /**
   * 将 DOM 元素导出为 PNG 图片（通用方法）：
   * - 通过 expandSelectors 指定哪些内层滚动容器需要被"撑开"为全高（业务方按需传入）
   * - 通过 hideSelectors 隐藏不该出现在截图里的元素（默认 [data-export-hidden]，业务方自行打标记）
   * - 通过 onBeforeCapture 在截图前对 DOM 做业务专属的临时改造（返回的清理函数会在截图后执行）
   * - 使用 html2canvas 按元素的真实尺寸截图，并按 padding 在四周补留白
   * - 完成后恢复全部样式 / display / 自定义改造
   */
  const exportPng = useCallback(async (
    element: HTMLElement,
    options?: IPngOptions,
  ): Promise<void> => {
    if (!element) return;
    const {
      filename = 'export.png',
      scale = 2,
      backgroundColor = '#ffffff',
      expandSelectors = [],
      hideSelectors = [],
      padding = { bottom: 40 },
      onBeforeCapture,
    } = options || {};

    // 归一化 padding：数字 → 四边相同；对象 → 按字段取值，缺省为 0
    const normalizedPadding = typeof padding === 'number'
      ? { top: padding, right: padding, bottom: padding, left: padding }
      : {
        top: padding.top ?? 0,
        right: padding.right ?? 0,
        bottom: padding.bottom ?? 0,
        left: padding.left ?? 0,
      };

    // ⭐ 必须在任何 DOM 操作（含 createExportOverlay）之前注册：
    // 蒙层使用 fixed + backdrop-filter，挂载到 body 时可能立刻引发 ali-react-table
    // 内部 ResizeObserver 触发 "loop completed with undelivered notifications" 良性警告。
    // 这是浏览器下一帧自然消化的、不影响功能的 warning，但 webpack-dev-server 的 error overlay
    // 会误识别为致命错误弹红框（且通常会按 message 去重，"第一次首发"必须一次性接住）。
    const suppressResizeLoopError = (e: ErrorEvent): void => {
      if (e?.message && /ResizeObserver loop/.test(e.message)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };
    window.addEventListener('error', suppressResizeLoopError, true);

    createExportOverlay(isEn, 'PNG');
    injectPngExportStyle();

    // 用 attribute 切换替代逐个 inline style 写入：
    //  - 代码更短、清理更简单（只需 removeAttribute，无需保存/还原 N 个属性 × M 个节点的原值）
    //  - 不污染元素既有内联样式
    //  - 多个并发调用相互不干扰：每次调用只追踪自己"实际加上"的节点，不重复加、不重复清
    const expandedNodes: HTMLElement[] = [];

    // 通用隐藏机制：业务方在按钮上打 data-export-hidden 即可避免被截入
    const defaultHideSelectors = [`[${PNG_EXPORT_HIDDEN_ATTR}]`];
    const allHideSelectors = [...defaultHideSelectors, ...hideSelectors];
    const hiddenNodes: HTMLElement[] = [];

    let beforeCaptureCleanup: (() => void) | undefined;
    const crossOriginImgs: { img: HTMLImageElement; originalSrc: string; }[] = [];

    try {
      // 1. 撑开调用方指定的内层滚动容器
      expandSelectors.forEach((selector) => {
        element.querySelectorAll<HTMLElement>(selector).forEach((node) => {
          if (node.hasAttribute(PNG_EXPORT_EXPANDING_ATTR)) return;
          node.setAttribute(PNG_EXPORT_EXPANDING_ATTR, 'true');
          expandedNodes.push(node);
        });
      });

      // 2. 隐藏不应被截入的元素（已经带 data-export-hidden 的不重复加，也不在清理时移除）
      allHideSelectors.forEach((selector) => {
        element.querySelectorAll<HTMLElement>(selector).forEach((node) => {
          if (node.hasAttribute(PNG_EXPORT_HIDDEN_ATTR)) return;
          node.setAttribute(PNG_EXPORT_HIDDEN_ATTR, '');
          hiddenNodes.push(node);
        });
      });

      // 3. 触发 resize + 等两帧 + 200ms 兜底，让虚拟滚动 / 图表按新尺寸完成渲染
      //    （某些虚拟滚动实现并未挂 ResizeObserver，必须显式 dispatch 才会重新渲染）
      window.dispatchEvent(new Event('resize'));
      await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
      await new Promise<void>((r) => setTimeout(r, 200));

      // 4. 调用方自定义钩子（业务专属临时改造，例如加 border-bottom）
      if (onBeforeCapture) {
        const result = await onBeforeCapture(element);
        if (typeof result === 'function') {
          beforeCaptureCleanup = result;
        }
      }

      // 4.5 预处理跨域图片：将跨域 <img> 转为 base64 dataURL，避免 html2canvas
      //     因 CORS 限制导致图片空白或 canvas 被污染（tainted）而无法 toBlob。
      //     - 同源 / data: / blob: 图片会被跳过（无需处理）
      //     - 跨域图片先通过 fetch(mode:'cors') 拿 blob → FileReader 转 dataURL
      //     - 如果 fetch 也因 CORS 失败，则回退到 crossOrigin='anonymous' + canvas 方式
      //     - 两种方式都失败则保留原 src，交给 html2canvas 的 useCORS 兜底
      const allImgs = element.querySelectorAll<HTMLImageElement>('img');
      await Promise.all(Array.from(allImgs).map(async (img) => {
        const {src} = img;
        if (!src || src.startsWith('data:') || src.startsWith('blob:')) return;
        // 判断是否同源
        try {
          const imgUrl = new URL(src, window.location.href);
          if (imgUrl.origin === window.location.origin) return;
        } catch {
          return;
        }
        // 记录原始 src 以便 finally 还原
        const originalSrc = src;
        // 方式一：fetch + blob → dataURL（更可靠，对 CORS 友好的 CDN 一次到位）
        try {
          const dataUrl = await fetchImageAsDataURL(src);
          if (dataUrl) {
            crossOriginImgs.push({ img, originalSrc });
            img.src = dataUrl;
            return;
          }
        } catch (e) {
          console.warn('[exportPng] fetch 转 base64 失败，尝试方式二:', src, e);
        }
        // 方式二：Image crossOrigin + canvas 手工转（兼容部分只允许 img 标签带 CORS 的场景）
        let tmpImg: HTMLImageElement | null = null;
        let cvs: HTMLCanvasElement | null = null;
        try {
          tmpImg = new Image();
          tmpImg.crossOrigin = 'anonymous';
          await new Promise<void>((resolve, reject) => {
            tmpImg!.onload = () => resolve();
            tmpImg!.onerror = () => reject(new Error('load failed'));
            tmpImg!.src = src;
          });
          cvs = document.createElement('canvas');
          cvs.width = tmpImg.naturalWidth;
          cvs.height = tmpImg.naturalHeight;
          const ctx = cvs.getContext('2d');
          if (ctx) {
            ctx.drawImage(tmpImg, 0, 0);
            const base64 = cvs.toDataURL('image/png');
            crossOriginImgs.push({ img, originalSrc });
            img.src = base64;
          }
        } catch {
          // 两种方式都失败，保留原 src 交给 html2canvas 的 useCORS 兜底
          console.warn('[exportPng] 跨域图片预处理失败，保留原 src:', src);
        } finally {
          // 主动释放临时 Image / Canvas 资源，加速 GC 回收
          if (tmpImg) {
            tmpImg.onload = null;
            tmpImg.onerror = null;
            tmpImg.src = '';
            tmpImg = null;
          }
          if (cvs) {
            cvs.width = 0;
            cvs.height = 0;
            cvs = null;
          }
        }
      }));

      // 5. 用 boundingRect（subpixel 精确）+ ceil + 2px 缓冲，避免末行边线被切
      const rect = element.getBoundingClientRect();
      const fullHeight = Math.ceil(Math.max(element.scrollHeight, rect.height)) + 2;

      // 注意：不再覆盖 windowWidth / windowHeight —— 否则 html2canvas 会按
      // 模拟的窗口尺寸去布局，元素若窄于该尺寸会在右侧产生大片留白。
      const baseCanvas = await html2canvas(element, {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor,
        logging: false,
        foreignObjectRendering: false,
        removeContainer: false,
        height: fullHeight,
      });

      // 6. 在外围拼接留白：新建更大的画布，填充背景色，再把原图绘制到指定偏移位置
      const padTopPx = Math.max(0, Math.round(normalizedPadding.top * scale));
      const padRightPx = Math.max(0, Math.round(normalizedPadding.right * scale));
      const padBottomPx = Math.max(0, Math.round(normalizedPadding.bottom * scale));
      const padLeftPx = Math.max(0, Math.round(normalizedPadding.left * scale));

      let canvas: HTMLCanvasElement = baseCanvas;
      if (padTopPx || padRightPx || padBottomPx || padLeftPx) {
        const paddedCanvas = document.createElement('canvas');
        paddedCanvas.width = baseCanvas.width + padLeftPx + padRightPx;
        paddedCanvas.height = baseCanvas.height + padTopPx + padBottomPx;
        const ctx = paddedCanvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);
          ctx.drawImage(baseCanvas, padLeftPx, padTopPx);
          canvas = paddedCanvas;
        }
      }

      // 7. 触发下载
      const finalFilename = filename.toLowerCase().endsWith('.png')
        ? filename
        : `${filename}.png`;

      await new Promise<void>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to convert canvas to blob'));
            return;
          }
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = finalFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          resolve();
        }, 'image/png', 1.0);
      });
    } catch (error) {
      console.error('导出 PNG 失败:', error);
      throw error;
    } finally {
      // 先执行 onBeforeCapture 返回的清理函数（业务专属临时改造的还原）
      if (typeof beforeCaptureCleanup === 'function') {
        try {
          beforeCaptureCleanup();
        } catch (e) {
          console.warn('exportPng beforeCaptureCleanup 执行失败:', e);
        }
      }

      // 还原跨域图片的原始 src，避免 base64 持续占用内存且影响页面后续交互
      crossOriginImgs.forEach(({ img, originalSrc }) => {
        img.src = originalSrc;
      });

      // 还原：只移除"本次实际加上的" attribute，
      // 调用前就有的 data-export-hidden（业务自己打的标记）保持不动
      expandedNodes.forEach((node) => node.removeAttribute(PNG_EXPORT_EXPANDING_ATTR));
      hiddenNodes.forEach((node) => node.removeAttribute(PNG_EXPORT_HIDDEN_ATTR));

      // 移除导出期间临时注入的 CSS 规则，让按钮上常驻的 data-export-hidden 标记重新无害化
      removePngExportStyle();

      // 通知组件按原尺寸重新渲染
      window.dispatchEvent(new Event('resize'));

      removeExportOverlay('PNG');

      // ⭐ 监听器延后 500ms 摘除：
      // dispatchEvent('resize') 触发的 ResizeObserver 回调是在下一帧（甚至下下帧）
      // 异步执行的，如果立即摘除监听器，这段尾巴里的 loop 警告又会漏到 dev overlay。
      setTimeout(() => {
        window.removeEventListener('error', suppressResizeLoopError, true);
      }, 500);
    }
  }, [isEn]);

  return { exportPdf, exportTxt, exportWord, exportPng };
};
